import React, { forwardRef } from "react";
import { styleConstants as C } from "~/style/styleConstants";
import type { ReactNode } from "react";
import type { ViewStyle } from "react-native";

export type WithLayoutProps = {
  /** borderRadiusSmall=4 borderRadiusMedium=8 borderRadiusLarge=16  */
  borderRadiusSmall?: boolean;
  /** borderRadiusSmall=4 borderRadiusMedium=8 borderRadiusLarge=16  */
  borderRadiusMedium?: boolean;
  /** borderRadiusSmall=4 borderRadiusMedium=8 borderRadiusLarge=16  */
  borderRadiusLarge?: boolean;

  aspectRatioOne?: boolean;

  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingExtraSmall?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingSmall?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingMedium?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingLarge?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingExtraLarge?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingHorizontalExtraSmall?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingHorizontalSmall?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingHorizontalMedium?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingHorizontalLarge?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingHorizontalExtraLarge?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingVerticalExtraSmall?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingVerticalSmall?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingVerticalMedium?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingVerticalLarge?: boolean;
  /** extraSmall=4 small=8 medium=12 large=16 extraLarge=24  */
  paddingVerticalExtraLarge?: boolean;
  centerContent?: boolean;

  justifyContentCenter?: boolean;
  justifyContentFlexStart?: boolean;
  justifyContentFlexEnd?: boolean;
  justifyContentSpaceBetween?: boolean;
  justifyContentSpaceAround?: boolean;
  justifyContentSpaceEvenly?: boolean;

  alignItemsFlexStart?: boolean;
  alignItemsFlexEnd?: boolean;
  alignItemsCenter?: boolean;
  alignItemsStretch?: boolean;
  alignItemsBaseline?: boolean;

  alignSelfFlexStart?: boolean;
  alignSelfFlexEnd?: boolean;
  alignSelfCenter?: boolean;
  alignSelfStretch?: boolean;
  alignSelfBaseline?: boolean;

  flex?: boolean;
  flexDirectionRow?: boolean;
  flexDirectionColumn?: boolean;
  flexDirectionRowReverse?: boolean;
  flexDirectionColumnReverse?: boolean;

  flexWrap?: boolean;

  absoluteTopLeft?: boolean;
  absoluteTopLeftSmall?: boolean;
  absoluteTopLeftMedium?: boolean;
  absoluteTopLeftLarge?: boolean;
  absoluteTopLeftExtraLarge?: boolean;

  absoluteTopRight?: boolean;
  absoluteTopRightSmall?: boolean;
  absoluteTopRightMedium?: boolean;
  absoluteTopRightLarge?: boolean;
  absoluteTopRightExtraLarge?: boolean;

  absoluteBottomLeft?: boolean;
  absoluteBottomLeftSmall?: boolean;
  absoluteBottomLeftMedium?: boolean;
  absoluteBottomLeftLarge?: boolean;
  absoluteBottomLeftExtraLarge?: boolean;

  absoluteBottomRight?: boolean;
  absoluteBottomRightSmall?: boolean;
  absoluteBottomRightMedium?: boolean;
  absoluteBottomRightLarge?: boolean;
  absoluteBottomRightExtraLarge?: boolean;

  colorBackgroundWhite?: boolean;
  colorBackgroundLight?: boolean;
  colorBackgroundLightDark?: boolean;
  colorBackgroundLightDarker?: boolean;
  colorBackgroundTheme?: boolean;
  colorBackgroundDarkLighter?: boolean;
  colorBackgroundDarkLight?: boolean;
  colorBackgroundDark?: boolean;
  colorBackgroundBlack?: boolean;
  colorBackgroundDanger?: boolean;
  colorBackgroundWarning?: boolean;
  colorBackgroundSuccess?: boolean;
  colorBackgroundGray?: boolean;
  colorBackgroundDisabled?: boolean;
  colorBackgroundBackdrop?: boolean;
  colorBackgroundBackdropWhite?: boolean;

  pointerEventsAuto?: boolean;
  pointerEventsNone?: boolean;
  pointerEventsBoxNone?: boolean;
  pointerEventsBoxOnly?: boolean;

  children?: ReactNode;
};

export function withLayoutProps<InputComponent extends React.ComponentType>(
  Component: InputComponent,
): React.ComponentType<
  Omit<React.ComponentPropsWithRef<InputComponent>, keyof WithLayoutProps> &
    WithLayoutProps
> {
  type NewProps = WithLayoutProps & { style: any };

  return forwardRef(
    (
      {
        borderRadiusSmall,
        borderRadiusMedium,
        borderRadiusLarge,

        paddingExtraSmall,
        paddingSmall,
        paddingMedium,
        paddingLarge,
        paddingExtraLarge,

        paddingHorizontalExtraSmall,
        paddingHorizontalSmall,
        paddingHorizontalMedium,
        paddingHorizontalLarge,
        paddingHorizontalExtraLarge,

        paddingVerticalExtraSmall,
        paddingVerticalSmall,
        paddingVerticalMedium,
        paddingVerticalLarge,
        paddingVerticalExtraLarge,

        centerContent,

        justifyContentCenter,
        justifyContentFlexStart,
        justifyContentFlexEnd,
        justifyContentSpaceBetween,
        justifyContentSpaceAround,
        justifyContentSpaceEvenly,

        alignItemsFlexStart,
        alignItemsFlexEnd,
        alignItemsCenter,
        alignItemsStretch,
        alignItemsBaseline,

        alignSelfFlexStart,
        alignSelfFlexEnd,
        alignSelfCenter,
        alignSelfStretch,
        alignSelfBaseline,

        flexDirectionRow,
        flexDirectionColumn,
        flexDirectionRowReverse,
        flexDirectionColumnReverse,

        absoluteTopLeft,
        absoluteTopLeftSmall,
        absoluteTopLeftMedium,
        absoluteTopLeftLarge,
        absoluteTopLeftExtraLarge,
        absoluteTopRight,
        absoluteTopRightSmall,
        absoluteTopRightMedium,
        absoluteTopRightLarge,
        absoluteTopRightExtraLarge,
        absoluteBottomLeft,
        absoluteBottomLeftSmall,
        absoluteBottomLeftMedium,
        absoluteBottomLeftLarge,
        absoluteBottomLeftExtraLarge,
        absoluteBottomRight,
        absoluteBottomRightSmall,
        absoluteBottomRightMedium,
        absoluteBottomRightLarge,
        absoluteBottomRightExtraLarge,

        colorBackgroundWhite,
        colorBackgroundLight,
        colorBackgroundLightDark,
        colorBackgroundLightDarker,
        colorBackgroundTheme,
        colorBackgroundDarkLighter,
        colorBackgroundDarkLight,
        colorBackgroundDark,
        colorBackgroundBlack,
        colorBackgroundDanger,
        colorBackgroundWarning,
        colorBackgroundSuccess,
        colorBackgroundGray,
        colorBackgroundDisabled,
        colorBackgroundBackdrop,
        colorBackgroundBackdropWhite,

        pointerEventsAuto,
        pointerEventsNone,
        pointerEventsBoxNone,
        pointerEventsBoxOnly,

        flex,
        flexWrap,
        aspectRatioOne,
        style: passThroughStyle,
        ...passThroughProps
      }: NewProps,
      ref,
    ) => {
      const style: ViewStyle = {};
      if (borderRadiusSmall) style.borderRadius = C.borderRadiusSmall;
      if (borderRadiusMedium) style.borderRadius = C.borderRadiusMedium;
      if (borderRadiusLarge) style.borderRadius = C.borderRadiusLarge;

      if (paddingExtraSmall) style.padding = C.spacingExtraSmall;
      if (paddingSmall) style.padding = C.spacingSmall;
      if (paddingMedium) style.padding = C.spacingMedium;
      if (paddingLarge) style.padding = C.spacingLarge;
      if (paddingExtraLarge) style.padding = C.spacingExtraLarge;

      if (paddingHorizontalExtraSmall)
        style.paddingHorizontal = C.spacingExtraSmall;
      if (paddingHorizontalSmall) style.paddingHorizontal = C.spacingSmall;
      if (paddingHorizontalMedium) style.paddingHorizontal = C.spacingMedium;
      if (paddingHorizontalLarge) style.paddingHorizontal = C.spacingLarge;
      if (paddingHorizontalExtraLarge)
        style.paddingHorizontal = C.spacingExtraLarge;

      if (paddingVerticalExtraSmall)
        style.paddingVertical = C.spacingExtraSmall;
      if (paddingVerticalSmall) style.paddingVertical = C.spacingSmall;
      if (paddingVerticalMedium) style.paddingVertical = C.spacingMedium;
      if (paddingVerticalLarge) style.paddingVertical = C.spacingLarge;
      if (paddingVerticalExtraLarge)
        style.paddingVertical = C.spacingExtraLarge;

      if (centerContent) {
        style.justifyContent = "center";
        style.alignItems = "center";
      }
      if (justifyContentCenter) style.justifyContent = "center";
      if (justifyContentFlexStart) style.justifyContent = "flex-start";
      if (justifyContentFlexEnd) style.justifyContent = "flex-end";
      if (justifyContentSpaceBetween) style.justifyContent = "space-between";
      if (justifyContentSpaceAround) style.justifyContent = "space-around";
      if (justifyContentSpaceEvenly) style.justifyContent = "space-evenly";

      if (alignItemsFlexStart) style.alignItems = "flex-start";
      if (alignItemsFlexEnd) style.alignItems = "flex-end";
      if (alignItemsCenter) style.alignItems = "center";
      if (alignItemsStretch) style.alignItems = "stretch";
      if (alignItemsBaseline) style.alignItems = "baseline";

      if (alignSelfFlexStart) style.alignSelf = "flex-start";
      if (alignSelfFlexEnd) style.alignSelf = "flex-end";
      if (alignSelfCenter) style.alignSelf = "center";
      if (alignSelfStretch) style.alignSelf = "stretch";
      if (alignSelfBaseline) style.alignSelf = "baseline";

      if (flexDirectionRow) style.flexDirection = "row";
      if (flexDirectionColumn) style.flexDirection = "column";
      if (flexDirectionRowReverse) style.flexDirection = "row-reverse";
      if (flexDirectionColumnReverse) style.flexDirection = "column-reverse";

      if (absoluteTopLeft) {
        style.position = "absolute";
        style.top = 0;
        style.left = 0;
      }
      if (absoluteTopLeftSmall) {
        style.position = "absolute";
        style.top = C.spacingSmall;
        style.left = C.spacingSmall;
      }
      if (absoluteTopLeftMedium) {
        style.position = "absolute";
        style.top = C.spacingMedium;
        style.left = C.spacingMedium;
      }
      if (absoluteTopLeftLarge) {
        style.position = "absolute";
        style.top = C.spacingLarge;
        style.left = C.spacingLarge;
      }
      if (absoluteTopLeftExtraLarge) {
        style.position = "absolute";
        style.top = C.spacingExtraLarge;
        style.left = C.spacingExtraLarge;
      }
      if (absoluteTopRight) {
        style.position = "absolute";
        style.top = 0;
        style.right = 0;
      }
      if (absoluteTopRightSmall) {
        style.position = "absolute";
        style.top = C.spacingSmall;
        style.right = C.spacingSmall;
      }
      if (absoluteTopRightMedium) {
        style.position = "absolute";
        style.top = C.spacingMedium;
        style.right = C.spacingMedium;
      }
      if (absoluteTopRightLarge) {
        style.position = "absolute";
        style.top = C.spacingLarge;
        style.right = C.spacingLarge;
      }
      if (absoluteTopRightExtraLarge) {
        style.position = "absolute";
        style.top = C.spacingExtraLarge;
        style.right = C.spacingExtraLarge;
      }
      if (absoluteBottomLeft) {
        style.position = "absolute";
        style.bottom = 0;
        style.left = 0;
      }
      if (absoluteBottomLeftSmall) {
        style.position = "absolute";
        style.bottom = C.spacingSmall;
        style.left = C.spacingSmall;
      }
      if (absoluteBottomLeftMedium) {
        style.position = "absolute";
        style.bottom = C.spacingMedium;
        style.left = C.spacingMedium;
      }
      if (absoluteBottomLeftLarge) {
        style.position = "absolute";
        style.bottom = C.spacingLarge;
        style.left = C.spacingLarge;
      }
      if (absoluteBottomLeftExtraLarge) {
        style.position = "absolute";
        style.bottom = C.spacingExtraLarge;
        style.left = C.spacingExtraLarge;
      }
      if (absoluteBottomRight) {
        style.position = "absolute";
        style.bottom = 0;
        style.right = 0;
      }
      if (absoluteBottomRightSmall) {
        style.position = "absolute";
        style.bottom = C.spacingSmall;
        style.right = C.spacingSmall;
      }
      if (absoluteBottomRightMedium) {
        style.position = "absolute";
        style.bottom = C.spacingMedium;
        style.right = C.spacingMedium;
      }
      if (absoluteBottomRightLarge) {
        style.position = "absolute";
        style.bottom = C.spacingLarge;
        style.right = C.spacingLarge;
      }
      if (absoluteBottomRightExtraLarge) {
        style.position = "absolute";
        style.bottom = C.spacingExtraLarge;
        style.right = C.spacingExtraLarge;
      }

      if (colorBackgroundWhite) style.backgroundColor = C.colorBackgroundWhite;
      else if (colorBackgroundLight)
        style.backgroundColor = C.colorBackgroundLight;
      else if (colorBackgroundLightDark)
        style.backgroundColor = C.colorBackgroundLightDark;
      else if (colorBackgroundLightDarker)
        style.backgroundColor = C.colorBackgroundLightDarker;
      else if (colorBackgroundTheme)
        style.backgroundColor = C.colorBackgroundTheme;
      else if (colorBackgroundDarkLighter)
        style.backgroundColor = C.colorBackgroundDarkLighter;
      else if (colorBackgroundDarkLight)
        style.backgroundColor = C.colorBackgroundDarkLight;
      else if (colorBackgroundDark)
        style.backgroundColor = C.colorBackgroundDark;
      else if (colorBackgroundBlack)
        style.backgroundColor = C.colorBackgroundBlack;
      else if (colorBackgroundDanger)
        style.backgroundColor = C.colorBackgroundDanger;
      else if (colorBackgroundWarning)
        style.backgroundColor = C.colorBackgroundWarning;
      else if (colorBackgroundSuccess)
        style.backgroundColor = C.colorBackgroundSuccess;
      else if (colorBackgroundGray)
        style.backgroundColor = C.colorBackgroundGray;
      else if (colorBackgroundDisabled)
        style.backgroundColor = C.colorBackgroundDisabled;
      else if (colorBackgroundBackdrop)
        style.backgroundColor = C.colorBackgroundBackdrop;
      else if (colorBackgroundBackdropWhite)
        style.backgroundColor = C.colorBackgroundBackdropWhite;

      if (flex) style.flex = 1;

      if (flexWrap) style.flexWrap = "wrap";

      if (aspectRatioOne) {
        style.aspectRatio = 1;
      }

      if (pointerEventsAuto) {
        style.pointerEvents = "auto";
      } else if (pointerEventsNone) {
        style.pointerEvents = "none";
      } else if (pointerEventsBoxNone) {
        style.pointerEvents = "box-none";
      } else if (pointerEventsBoxOnly) {
        style.pointerEvents = "box-only";
      }

      return (
        <Component
          ref={ref}
          style={[style, passThroughStyle]}
          {...(passThroughProps as any)}
        />
      );
    },
  ) as any;
}
